import { useContext, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom'
import { ErrorState, LoadingState, Toaster, toast, useResource } from '@retink/web-ui'
import { AuthProvider, AuthContext, isPlatformUser, fetchMe } from '@retink/web-auth'
import { api, identityAuthUrl, signOut, WORKSPACE_URL } from './lib/api'
import { ConsoleFrame } from './components/ConsoleFrame'
import { OrganizationFrame } from './components/OrganizationFrame'
import { OperationsFrame } from './components/OperationsFrame'
import { TenantAdminShell } from './components/TenantAdminShell'
import { TenantOverview } from './pages/tenant-home/Overview'
import { TenantTeam } from './pages/tenant-home/Team'
import { Orgs } from './pages/Orgs'
import { OrgTenants } from './pages/org/OrgTenants'
import { OrgDashboard } from './pages/org/OrgDashboard'
import { OrgUsers } from './pages/org/OrgUsers'
import { OrgIdp } from './pages/org/OrgIdp'
import { OrgActivity } from './pages/org/OrgActivity'
import { Users } from './pages/Users'
import { Products } from './pages/Products'
import { TenantProducts, TenantFeatures, TenantAdapters, TenantSignIn } from './pages/tenant/sections'
import { PlatformTenantShell } from './components/PlatformTenantShell'
import { Audit } from './pages/Audit'
import { Settings } from './pages/Settings'
import { Idp } from './pages/Idp'

const ID_URL = import.meta.env['VITE_ID_URL'] ?? 'http://id.retink.test:9010'

// The staff/org administration surface. The tenant application (the workspace —
// launcher, team, account) lives on apps/workspace at the app. host; everything
// tenant-USER-facing redirects there.
export function App() {
  return (
    <AuthProvider idUrl={ID_URL} fetchMe={() => fetchMe(api)} unauthenticated={<ConsoleSignIn />}>
      <Toaster />
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <SsoFeedback />
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          {/* New console: task-led global frame + one organization route tree. */}
          <Route element={<ConsoleFrame />}>
            <Route element={<PlatformOnly />}>
              <Route path="/organizations" element={<Orgs />} />
              <Route path="/staff" element={<Users staffOnly />} />
              <Route path="/operations" element={<OperationsFrame />}>
                <Route index element={<Navigate to="services" replace />} />
                <Route path="services" element={<Products />} />
                <Route path="audit" element={<Audit />} />
                <Route path="identity-providers" element={<Idp />} />
                <Route path="identities" element={<Users />} />
                <Route path="workspaces/:tenantId/sign-in" element={<TenantSignIn />} />
              </Route>
            </Route>
            <Route path="/organizations/:orgId" element={<OrganizationFrame />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OrgDashboard />} />
              <Route path="people" element={<OrgUsers />} />
              <Route path="identity" element={<OrgIdp />} />
              <Route path="workspaces" element={<OrgTenants />} />
              <Route path="activity" element={<OrgActivity />} />
            </Route>
            <Route path="/account" element={<Settings />} />
          </Route>

          {/* Old tenant configuration remains reachable for support, but is no
              longer part of organization onboarding or primary navigation. */}
          <Route path="/platform/t/:tenantId/signin" element={<LegacyTenantIdentityRedirect />} />
          <Route path="/platform/t/:tenantId" element={<PlatformTenantShell />}>
            <Route index element={<LegacyTenantIdentityRedirect />} />
            <Route path="products" element={<TenantProducts />} />
            <Route path="features" element={<TenantFeatures />} />
            <Route path="adapters" element={<TenantAdapters />} />
          </Route>

          {/* Tenant-home — a tenant admin's own tenant surface (no Platform strip);
              platform/org admins who drill in land here too. */}
          <Route path="/tenant-home" element={<TenantAdminShell />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<TenantOverview />} />
            <Route path="team" element={<TenantTeam />} />
            <Route path="account" element={<Settings />} />
          </Route>

          {/* Stable redirects while bookmarks move to the new route names. */}
          <Route path="/platform" element={<Navigate to="/organizations" replace />} />
          <Route path="/platform/dashboard" element={<Navigate to="/organizations" replace />} />
          <Route path="/platform/orgs" element={<Navigate to="/organizations" replace />} />
          <Route path="/platform/tenants" element={<Navigate to="/organizations" replace />} />
          <Route path="/platform/users" element={<Navigate to="/operations/identities" replace />} />
          <Route path="/platform/products" element={<Navigate to="/operations/services" replace />} />
          <Route path="/platform/idp" element={<Navigate to="/operations/identity-providers" replace />} />
          <Route path="/platform/audit" element={<Navigate to="/operations/audit" replace />} />
          <Route path="/platform/account" element={<Navigate to="/account" replace />} />
          <Route path="/org/:orgId" element={<LegacyOrganizationRedirect section="overview" />} />
          <Route path="/org/:orgId/dashboard" element={<LegacyOrganizationRedirect section="overview" />} />
          <Route path="/org/:orgId/users" element={<LegacyOrganizationRedirect section="people" />} />
          <Route path="/org/:orgId/idp" element={<LegacyOrganizationRedirect section="identity" />} />
          <Route path="/org/:orgId/tenants" element={<LegacyOrganizationRedirect section="workspaces" />} />
          <Route path="/org/:orgId/activity" element={<LegacyOrganizationRedirect section="activity" />} />
          <Route path="/org/:orgId/account" element={<Navigate to="/account" replace />} />

          {/* The tenant workspace moved to apps/workspace (app. host) — forward
              old bookmarks/deep links there, path intact. */}
          <Route path="/t/*" element={<TenantForwarder />} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

function ConsoleSignIn() {
  const pageUrl = new URL(window.location.href)
  const error = pageUrl.searchParams.get('sso_error')
  pageUrl.searchParams.delete('sso_error')
  pageUrl.searchParams.delete('sso')
  pageUrl.searchParams.delete('sso_consent')
  const startUrl = identityAuthUrl(
    `/api/auth/sso/microsoft/start?return=${encodeURIComponent(pageUrl.toString())}`,
  )

  return (
    <main className="flex min-h-full items-center justify-center bg-sidebar px-4 text-sidebar-foreground">
      <div className="w-full max-w-sm rounded-xl border border-sidebar-border bg-card p-6 text-card-foreground shadow-2xl">
        <div className="mb-6 text-center">
          <div className="font-display text-2xl font-semibold tracking-tight">Retink</div>
          <div className="mt-1 text-sm text-muted-foreground">Sign in to Console</div>
        </div>
        {error && (
          <div role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Microsoft sign-in failed. Please try again.
          </div>
        )}
        <a
          href={startUrl}
          className="flex h-10 w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Sign in with Microsoft
        </a>
      </div>
    </main>
  )
}

const SSO_ERRORS: Record<string, string> = {
  access_denied: 'Microsoft sign-in was cancelled or denied.',
  consent_not_granted: 'Microsoft admin consent was not granted.',
  directory_mismatch: 'The Microsoft account belongs to a different directory.',
  invite_invalid: 'This owner setup link is invalid or has expired.',
  invalid_state: 'The sign-in session expired. Start owner setup again.',
  token_verification_failed: 'Microsoft sign-in could not be verified. Try again or contact platform support.',
  account_inactive: 'This account is inactive.',
}

function SsoFeedback() {
  const [params, setParams] = useSearchParams()
  const error = params.get('sso_error')
  const consent = params.get('sso_consent')
  const activated = params.get('sso')

  useEffect(() => {
    if (error) toast.error(SSO_ERRORS[error] ?? `Microsoft sign-in failed (${error}).`)
    if (consent === 'granted') toast.success('Microsoft admin consent granted')
    if (activated === 'activated') toast.success('Microsoft sign-in enabled')
    if (!error && !consent && !activated) return
    const next = new URLSearchParams(params)
    next.delete('sso_error')
    next.delete('sso_consent')
    next.delete('sso')
    setParams(next, { replace: true })
  }, [error, consent, activated, params, setParams])

  return null
}

// Sends each role to its default surface. Order matches the locked 3-role
// precedence (identity's home-resolver): platform staff → org admin → tenant admin
// (the console tenant-home) → a plain tenant member (their application lives on the
// workspace host, cross-host) → orphan fallback.
// A workspace URL must be absolute and cross-host — never a relative path, or
// `${WORKSPACE_URL}/dashboard` collapses to a same-origin "/dashboard" that this
// very catch-all re-handles, looping forever. Guards against a missing/empty
// VITE_WORKSPACE_URL build arg (which is how the focail console once shipped).
const WORKSPACE_OK = /^https?:\/\//.test(WORKSPACE_URL)

function RootRedirect() {
  const { user } = useContext(AuthContext)
  // Only a PLAIN tenant member goes cross-host to the workspace. Admins of any
  // scope stay in the console (platform/org → their view; tenant → tenant-home).
  const goWorkspace = !!user && !isPlatformUser(user)
    && user.orgRole !== 'admin' && user.tenantRole !== 'admin'
    && !!user.tenantId && WORKSPACE_OK
  const workspaceHome = goWorkspace ? `${WORKSPACE_URL}/dashboard` : null

  useEffect(() => {
    if (workspaceHome) window.location.replace(workspaceHome)
  }, [workspaceHome])

  if (!user) return null
  if (isPlatformUser(user)) return <Navigate to="/organizations" replace />
  if (user.orgRole === 'admin' && user.orgId) return <Navigate to={`/organizations/${user.orgId}/overview`} replace />
  if (user.tenantRole === 'admin') return <Navigate to="/tenant-home" replace />
  if (workspaceHome) {
    return (
      <div className="flex h-full items-center justify-center bg-background text-sm text-muted-foreground">
        Taking you to your workspace…
      </div>
    )
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-background text-sm text-muted-foreground">
      <div>Your account has no workspace yet — ask an administrator for access.</div>
      <button onClick={signOut} className="text-primary hover:underline">Sign out</button>
    </div>
  )
}

function PlatformOnly() {
  const { user } = useContext(AuthContext)
  return isPlatformUser(user) ? <Outlet /> : <Navigate to="/" replace />
}

function LegacyOrganizationRedirect({ section }: { section: string }) {
  const { orgId } = useParams<{ orgId: string }>()
  return <Navigate to={`/organizations/${orgId}/${section}`} replace />
}

function LegacyTenantIdentityRedirect() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const { data: tenant, loading, error, reload } = useResource<{ org_id: string }>(
    () => api.get(`/api/admin/tenants/${tenantId}`),
    [tenantId],
  )
  if (loading) return <div className="p-8"><LoadingState /></div>
  if (error || !tenant) return <div className="p-8"><ErrorState message={error ?? 'Workspace not found'} onRetry={reload} /></div>
  return <Navigate to={`/organizations/${tenant.org_id}/identity`} replace />
}

function TenantForwarder() {
  useEffect(() => {
    if (WORKSPACE_OK) window.location.replace(`${WORKSPACE_URL}${window.location.pathname}${window.location.search}`)
  }, [])
  return (
    <div className="flex h-full items-center justify-center bg-background text-sm text-muted-foreground">
      Taking you to your workspace…
    </div>
  )
}
